<template>
  <div class="template-group">
    <div v-if="!editMode">
      <div v-if="showLabel">
        <label :class="formLabelClass"
               class="form-label"
               :data-content="label">
          <span>{{ label }}</span>
        </label>
      </div>
      <div class="word-break">{{ v || '-' }}</div>
    </div>
    <div class="position-relative" ref="anchor" v-if="editMode">
      <AutosizeTextArea
          ref="textarea"
          :showlabel="showLabel"
          :placeholder="placeholder || label"
          :rows="rows"
          :labelclass="formLabelClass"
          :label="label"
          :readonly="readonly"
          v-model="v"
          @change="$emit('change')"
          :hasSaveBtn="hasSaveBtn"
          :dictation="dictation"
          :minH="isMobile ? 32 : minHeight"
          :maxH="maxHeight"
      >
        <template #action-button
                  v-if="hasSaveBtn">
          <button :disabled="disabledForTemplateSave"
                  :title="$t('save_as_template')"
                  v-b-tooltip.hover
                  class="small-template-sav p-0"
                  @click="addAsNewTemplate">
            <SaveSvg :class="{
              'svg-disabled': disabledForTemplateSave,
              'svg-enabled': !disabledForTemplateSave
            }"/>
          </button>
        </template>
      </AutosizeTextArea>
      <teleport to="#app">
        <div class="autocomplete-dropdown bg-white shadow-sm rounded"
             v-if="found.length > 0"
             :style="dropdownStyle"
             v-click-outside="clickedOutside">
          <a class="d-block pointer"
             @click="select(f)"
             v-for="(f, find) in found"
             :key="`found-${find}`">{{ f.title }}</a>
        </div>
      </teleport>
    </div>
    <div class="text-right mt-3"
         v-if="saveAsTemplate && bigSaveTemplate">
      <button :disabled="disabledForTemplateSave"
              class="btn-themed btn-themed-outline btn-themed-squared d-inline-block"
              @click="addAsNewTemplate">
        {{ $t('save_as_template') }}
      </button>
    </div>
  </div>
</template>

<script>

import { EloquentService } from "@/services/api/eloquent.api.service"
import AutosizeTextArea from "@/components/form/AutosizeTextArea"
import {mapState} from "vuex"
import SaveSvg from '@/assets/svg-vue/general/save2.svg'
import {appendFormdata} from "@/extensions/prototypes/prototypes"

export default {
  components: {
    AutosizeTextArea,
    SaveSvg
  },
  emits: ['input', 'change'],
  data() {
    return {
      v: this.value,
      found: [],
      timer: null,
      loading: false,
      dropdownPosition: { top: 0, left: 0, width: 0 }
    }
  },
  props: {
    label: {},
    placeholder: {},
    value: {},
    model: {
      type: String,
      required: true
    },
    dictation: {
      type: Boolean,
      default: false
    },
    saveAsTemplate: {
      type: Boolean,
      default: true
    },
    bigSaveTemplate: {
      type: Boolean,
      default: true
    },
    disableSearch: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    rows: {
      default: 'auto'
    },
    minHeight: {
      type: Number,
      default: 46
    },
    maxHeight: {
      type: Number,
      default: 110,
    },
    editMode: {
      type: Boolean,
      default: true
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    formLabelClass: {
      type: String,
    },
  },
  methods: {
    updateDropdownPosition() {
      const anchor = this.$refs.anchor
      if(!anchor) return
      const rect = anchor.getBoundingClientRect()
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft
      const scrollY = window.pageYOffset || document.documentElement.scrollTop
      this.dropdownPosition = {
        top: rect.bottom + scrollY,
        left: rect.left + scrollX,
        width: rect.width
      }
    },
    search() {
      if(this.disableSearch) {
        return
      }
      if(!this.searchString) {
        this.found = []
        return
      }
      this.loading = true
      EloquentService.autocomplete(this.model, this.searchString, 10, false).then(res => {
        this.found = res.data.result
        this.loading = false
      })
    },
    clickedOutside() {
      this.found = []
    },
    select(c) {
      const templateText = c.title.toLowerCase()
      const ourText = this.v.toLowerCase()
      let overlapString = ''
      for (let i = 0; i < templateText.length; i++) {
        const newString = overlapString + templateText[i]
        if (this.v.includes(newString)) {
          overlapString = newString
        } else {
          break
        }
      }
      if(!overlapString) overlapString = this.searchString

      if(overlapString && ourText.endsWith(overlapString)) {
        let beginningText = this.v.slice(0, -overlapString.length)
        if(beginningText && !beginningText.endsWith('\n')) beginningText += '\n'
        this.v = beginningText + c.title + ' '
        this.found = []
        this.$nextTick(() => this.$refs.textarea?.focusCustom())
        return
      }
      let arr = this.v.split(' ')
      let len = arr.length
      let strToAdd = c.title + ' '
      if(arr[(len - 1)].includes('\n')) {
        strToAdd = arr[(len - 1)].split('\n')[0] + '\n' + strToAdd
      } else {
        if(len > 1) {
          strToAdd = ' ' + strToAdd
        }
      }
      arr[(len - 1)] = strToAdd
      this.v = arr.join(' ')
      this.found = []
      this.$nextTick(() => this.$refs.textarea?.focusCustom())
    },
    addAsNewTemplate() {
      let formData = new FormData()
      formData = appendFormdata(formData, {
        title: this.v
      })
      this.loading = true
      EloquentService.create(this.model, formData).then(() => {
        this.loading = false
      })
    }
  },
  watch: {
    found(val) {
      if(val.length > 0) {
        this.$nextTick(() => this.updateDropdownPosition())
      }
    },
    v(val) {
      this.$emit('input', val)
      if(this.timer) {
        clearTimeout(this.timer)
      }
      if(this.disableSearch) {
        return
      }
      this.timer = setTimeout(() => {
        this.search()
      }, 600)
    },
    value(val) {
      this.v = val
    }
  },
  computed: {
    searchString() {
      if(!this.v) return this.v
      const n = Math.max(this.v.lastIndexOf(' '), this.v.lastIndexOf('\n'))
      return this.v.substring(n + 1)
    },
    ...mapState({
      isMobile: state => state.dom.isMobile
    }),
    hasSaveBtn() {
      return this.saveAsTemplate && !this.bigSaveTemplate
    },
    disabledForTemplateSave() {
      return this.loading || !this.v
    },
    dropdownStyle() {
      return {
        position: 'absolute',
        top: `${this.dropdownPosition.top}px`,
        left: `${this.dropdownPosition.left}px`,
        width: `${this.dropdownPosition.width}px`,
        zIndex: 1060
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateDropdownPosition, true)
    window.addEventListener('resize', this.updateDropdownPosition)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
    window.removeEventListener('resize', this.updateDropdownPosition)
  }
}
</script>

<style scoped lang="scss">
.small-template-save {
  top: 3px;
  right: 33px;
  &:disabled {
    opacity: 0.5;
  }
  svg {
    width: 32px;
    height: 32px;
    fill: $gray-icon;
  }
}
.svg-disabled {
  fill: rgba(0,0,0,0.2);
}
.svg-enabled {
  fill: #6C757D;
}

@media screen and (max-width: 768px) {
  .small-template-save {
    padding-top: 1px;
    top: 0;
    right: 27px;
    svg {
      width: 13px;
      height: 13px;
    }
  }
}
</style>
